export const materialPresentationTypeValues = ['SW', 'SCL', 'SCS', 'SC25', 'SP'] as const;
export type MaterialPresentationType = (typeof materialPresentationTypeValues)[number];

export const materialCaptureUnitValues = ['lb', 'piece'] as const;
export type MaterialCaptureUnit = (typeof materialCaptureUnitValues)[number];

export const materialCaptureTypeValues = ['weight', 'count'] as const;
export type MaterialCaptureType = (typeof materialCaptureTypeValues)[number];

export const materialOverrideScopeValues = ['company', 'store', 'location'] as const;
export type MaterialOverrideScope = (typeof materialOverrideScopeValues)[number];

export const materialCatalogSourceValues = ['global', 'local'] as const;
export type MaterialCatalogSource = (typeof materialCatalogSourceValues)[number];

export interface MaterialPresentationBase {
  type: MaterialPresentationType;
  unit: MaterialCaptureUnit;
  captureType: MaterialCaptureType;
  allowsFractional: boolean;
  isCrvEligible: boolean;
  isReportedToDoriis: boolean;
  // Optional catalog default ordering metadata.
  presentationOrder?: number | null;
}

export interface GlobalSwPresentationDto extends MaterialPresentationBase {
  type: 'SW';
  baseRatePerLb: number;
}

export interface GlobalScPresentationDto extends MaterialPresentationBase {
  type: 'SCL' | 'SCS' | 'SC25';
  depositPerPiece: number;
}

export interface GlobalSpPresentationDto extends MaterialPresentationBase {
  type: 'SP';
  ratePerLb: number;
}

export type GlobalMaterialPresentationDto =
  | GlobalSwPresentationDto
  | GlobalScPresentationDto
  | GlobalSpPresentationDto;

export interface ResolvedSwPresentationDto extends MaterialPresentationBase {
  type: 'SW';
  baseRatePerLb: number;
  premiumPerLb: number;
  effectiveRatePerLb: number;
}

export interface ResolvedScPresentationDto extends MaterialPresentationBase {
  type: 'SCL' | 'SCS' | 'SC25';
  depositPerPiece: number;
}

export interface ResolvedSpPresentationDto extends MaterialPresentationBase {
  type: 'SP';
  ratePerLb: number;
}

export type ResolvedMaterialPresentationDto =
  | ResolvedSwPresentationDto
  | ResolvedScPresentationDto
  | ResolvedSpPresentationDto;

export interface GlobalMaterialDto {
  id: string;
  code: string | null;
  displayName: string;
  // Optional catalog default ordering metadata.
  categoryOrder?: number | null;
  materialOrder?: number | null;
  isActive: boolean;
  isCrvEligible: boolean;
  maxPiecesPerLine: number;
  dailyCrvLimitLbs: number | null;
  presentations: GlobalMaterialPresentationDto[];
  effectiveFrom: string; // ISO date (YYYY-MM-DD)
  effectiveTo: string | null; // ISO date (YYYY-MM-DD)
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface MaterialOverrideDto {
  id: string;
  companyId: string;
  scope: MaterialOverrideScope;
  storeId: string | null;
  locationId: string | null;
  materialId: string;
  displayName: string | null;
  isActive: boolean | null;
  localCode: string | null;
  swPremiumPerLb: number | null;
  spRatePerLb: number | null;
  maxPiecesPerLine: number | null;
  dailyCrvLimitLbs: number | null;
  effectiveFrom: string; // ISO date (YYYY-MM-DD)
  effectiveTo: string | null; // ISO date (YYYY-MM-DD)
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface GlobalMaterialPriceVersionRow {
  id: string;
  materialId: string;
  presentationType: MaterialPresentationType;
  baseRatePerLb: number | null;
  depositPerPiece: number | null;
  ratePerLb: number | null;
  effectiveAtUtc: string; // ISO datetime
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface MaterialPriceOverrideVersionRow {
  id: string;
  companyId: string;
  scope: 'company' | 'store';
  storeId: string | null;
  materialId: string;
  swPremiumPerLb: number | null;
  spRatePerLb: number | null;
  effectiveAtUtc: string; // ISO datetime
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface ListGlobalMaterialPriceVersionsRequest {
  companyId: string;
  materialId?: string;
  presentationType?: MaterialPresentationType;
}

export interface ListGlobalMaterialPriceVersionsResponse {
  items: GlobalMaterialPriceVersionRow[];
}

export interface ListMaterialPriceOverrideVersionsRequest {
  companyId: string;
  materialId?: string;
  scope?: 'company' | 'store';
  storeId?: string;
}

export interface ListMaterialPriceOverrideVersionsResponse {
  items: MaterialPriceOverrideVersionRow[];
}

export interface ScheduleMaterialPriceOverrideVersionRequest {
  companyId: string;
  materialId: string;
  scope: 'company' | 'store';
  storeId?: string | null;
  effectiveOn: string; // ISO date (YYYY-MM-DD), interpreted in effectiveStoreId timezone
  effectiveStoreId: string;
  swPremiumPerLb?: number | null;
  spRatePerLb?: number | null;
}

export interface ScheduleMaterialPriceOverrideVersionResponse {
  version: MaterialPriceOverrideVersionRow;
}

export interface ResolvedMaterialDto {
  id: string;
  source: MaterialCatalogSource;
  displayName: string;
  code: string | null;
  // Optional catalog default ordering metadata for global sources.
  categoryOrder?: number | null;
  materialOrder?: number | null;
  isActive: boolean;
  isCrvEligible: boolean;
  maxPiecesPerLine: number;
  dailyCrvLimitLbs: number | null;
  presentations: ResolvedMaterialPresentationDto[];
  overrideScope: MaterialOverrideScope | null;
}

export interface LocalMaterialDto {
  id: string;
  companyId: string;
  storeId: string;
  locationId: string;
  stationId: string;
  source: 'local';
  code: string | null;
  displayName: string;
  isActive: boolean;
  isCrvEligible: boolean;
  maxPiecesPerLine: number;
  dailyCrvLimitLbs: number | null;
  presentations: GlobalMaterialPresentationDto[];
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface ListGlobalMaterialsRequest {
  companyId: string;
  effectiveOn?: string; // ISO date (YYYY-MM-DD)
  includeInactive?: boolean;
}

export interface ListGlobalMaterialsResponse {
  items: GlobalMaterialDto[];
}

export interface GetGlobalMaterialRequest {
  companyId: string;
  materialId: string;
}

export interface GetGlobalMaterialResponse {
  material: GlobalMaterialDto;
}

export interface CreateGlobalMaterialRequest {
  displayName: string;
  code?: string | null;
  isActive: boolean;
  isCrvEligible: boolean;
  maxPiecesPerLine: number;
  dailyCrvLimitLbs?: number | null;
  presentations: GlobalMaterialPresentationDto[];
  effectiveFrom: string; // ISO date (YYYY-MM-DD)
  effectiveTo?: string | null; // ISO date (YYYY-MM-DD)
}

export interface CreateGlobalMaterialResponse {
  material: GlobalMaterialDto;
}

export interface UpdateGlobalMaterialRequest {
  materialId: string;
  displayName?: string;
  code?: string | null;
  isActive?: boolean;
  isCrvEligible?: boolean;
  maxPiecesPerLine?: number;
  dailyCrvLimitLbs?: number | null;
  presentations?: GlobalMaterialPresentationDto[];
  effectiveFrom?: string; // ISO date (YYYY-MM-DD)
  effectiveTo?: string | null; // ISO date (YYYY-MM-DD)
}

export interface UpdateGlobalMaterialResponse {
  material: GlobalMaterialDto;
}

export interface ListMaterialOverridesRequest {
  companyId: string;
  materialId?: string;
  storeId?: string;
  locationId?: string;
  effectiveOn?: string; // ISO date (YYYY-MM-DD)
}

export interface ListMaterialOverridesResponse {
  items: MaterialOverrideDto[];
}

export interface CreateMaterialOverrideRequest {
  companyId: string;
  materialId: string;
  scope: MaterialOverrideScope;
  storeId?: string | null;
  locationId?: string | null;
  displayName?: string | null;
  isActive?: boolean | null;
  localCode?: string | null;
  swPremiumPerLb?: number | null;
  spRatePerLb?: number | null;
  maxPiecesPerLine?: number | null;
  dailyCrvLimitLbs?: number | null;
  effectiveFrom: string; // ISO date (YYYY-MM-DD)
  effectiveTo?: string | null; // ISO date (YYYY-MM-DD)
}

export interface CreateMaterialOverrideResponse {
  override: MaterialOverrideDto;
}

export interface UpdateMaterialOverrideRequest {
  companyId: string;
  overrideId: string;
  displayName?: string | null;
  isActive?: boolean | null;
  localCode?: string | null;
  swPremiumPerLb?: number | null;
  spRatePerLb?: number | null;
  maxPiecesPerLine?: number | null;
  dailyCrvLimitLbs?: number | null;
  effectiveFrom?: string; // ISO date (YYYY-MM-DD)
  effectiveTo?: string | null; // ISO date (YYYY-MM-DD)
}

export interface UpdateMaterialOverrideResponse {
  override: MaterialOverrideDto;
}

export interface ListResolvedMaterialsRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  stationId?: string;
  effectiveOn?: string; // ISO date (YYYY-MM-DD)
  includeInactive?: boolean;
}

export interface ListResolvedMaterialsResponse {
  items: ResolvedMaterialDto[];
}

export interface ListLocalMaterialsRequest {
  companyId: string;
  storeId: string;
  locationId: string;
  stationId: string;
  includeInactive?: boolean;
}

export interface ListLocalMaterialsResponse {
  items: LocalMaterialDto[];
}

export interface CreateLocalMaterialRequest {
  companyId: string;
  storeId: string;
  locationId: string;
  stationId: string;
  displayName: string;
  code?: string | null;
  isActive: boolean;
  isCrvEligible: boolean;
  maxPiecesPerLine: number;
  dailyCrvLimitLbs?: number | null;
  presentations: GlobalMaterialPresentationDto[];
}

export interface CreateLocalMaterialResponse {
  material: LocalMaterialDto;
}

export interface UpdateLocalMaterialRequest {
  companyId: string;
  localMaterialId: string;
  displayName?: string;
  code?: string | null;
  isActive?: boolean;
  isCrvEligible?: boolean;
  maxPiecesPerLine?: number;
  dailyCrvLimitLbs?: number | null;
  presentations?: GlobalMaterialPresentationDto[];
}

export interface UpdateLocalMaterialResponse {
  material: LocalMaterialDto;
}
